/**
 * Helpers that copy peer shared-memory snapshots into local stores on startup.
 */

/**
 * How long a collect waits for peers.
 * @type {number}
 */
export const COLLECT_MEMORIES_TIMEOUT_MS = 3000;

/**
 * Wait for this replica's own multicast, and for more peers after the first reply.
 * @type {number}
 */
export const SYNC_ECHO_GRACE_MS = 200;

/**
 * Upper bound for the admin status screen's expectedReplicas hint.
 * @type {number}
 */
export const MAX_EXPECTED_REPLICAS = 20;

/**
 * Live mutations recorded during startup sync and replayed after the peer snapshot.
 * @enum {string}
 */
export const SYNC_MUTATION = {
    SET_CANDEX: 'set_candex',
    SET_NESTBUY: 'set_nestbuy',
    DELETE_NESTBUY: 'delete_nestbuy',
    SET_CXML: 'set_cxml',
    DELETE_CXML: 'delete_cxml',
    SET_ALIAS: 'set_alias',
    ADD_PR: 'add_pr',
    DELETE_PR: 'delete_pr',
    SET_USER_LINK: 'set_user_link',
    UNSUBSCRIBE_FRONTEND: 'unsubscribe_frontend',
    UNLOCK_ALL_PRS: 'unlock_all_prs',
    OPEN_PRS: 'open_prs',
};

/**
 * @typedef {Object} PendingCollection
 * @property {Map<string, Object>} results snapshots keyed by backend id, self included
 * @property {number} expected how many replicas must answer before the collection completes
 * @property {boolean} sawOwnMulticast whether this replica observed its own collect multicast
 * @property {function(): void} done complete the collection
 * @property {function(): void=} armPeerGrace startup only: finish soon after the first peer
 */

/**
 * @param {number} ms
 * @return {Promise<void>}
 */
function defaultDelay(ms) {
    return new Promise((resolve) => {
        setTimeout(resolve, ms);
    });
}

/**
 * Normalize a Map, plain object, or array of pairs into entries.
 *
 * @param {*} raw
 * @return {Array<[*, *]>}
 */
export function toEntries(raw) {
    if (!raw) {
        return [];
    }
    if (raw instanceof Map) {
        return Array.from(raw.entries());
    }
    if (Array.isArray(raw)) {
        return raw.filter((pair) => Array.isArray(pair) && pair.length >= 2);
    }
    if (typeof raw === 'object') {
        return Object.entries(raw);
    }
    return [];
}

/**
 * Normalize a set-like value (Set, array, or plain object) into a list of values.
 *
 * @param {*} raw
 * @return {Array<*>}
 */
export function toValues(raw) {
    if (!raw) {
        return [];
    }
    if (raw instanceof Set) {
        return Array.from(raw.values());
    }
    if (Array.isArray(raw)) {
        return raw;
    }
    if (typeof raw === 'object') {
        return Object.values(raw);
    }
    return [];
}

/**
 * Copy entries into a Map or TimeoutMap via set. TimeoutMap.set restarts the TTL.
 *
 * @param {{set: function(*, *): *}} target
 * @param {*} raw
 * @return {number} number of entries written
 */
export function applyEntries(target, raw) {
    if (!target || typeof target.set !== 'function') {
        return 0;
    }
    let count = 0;
    for (const [key, value] of toEntries(raw)) {
        target.set(key, value);
        count++;
    }
    return count;
}

/**
 * Add values into a Set.
 *
 * @param {{add: function(*): *}} target
 * @param {*} raw
 * @return {number} number of values written, including ones already present
 */
export function applySetValues(target, raw) {
    if (!target || typeof target.add !== 'function') {
        return 0;
    }
    let count = 0;
    for (const value of toValues(raw)) {
        target.add(value);
        count++;
    }
    return count;
}

/**
 * Return peer snapshots from a collection, excluding this replica.
 *
 * @param {string} selfBackendId
 * @param {*} snapshots
 * @return {Array<Object>}
 */
export function peerSnapshots(selfBackendId, snapshots) {
    const peers = [];
    for (const [id, snapshot] of toEntries(snapshots)) {
        if (!snapshot || typeof snapshot !== 'object') {
            continue;
        }
        const peerId = snapshot.backendId ?? id;
        if (!peerId || peerId === selfBackendId) {
            continue;
        }
        peers.push(snapshot);
    }
    return peers;
}

/**
 * Copy one All RA snapshot into the local stores. NestBuy is read from nestBuySessionInfo.
 *
 * @param {{candexSessionIds: {set: Function}, nestBuySession: {set: Function}, cxmlBuyerCookies: {set: Function}, systemAliasByUser: {set: Function}}} stores
 * @param {Object} snapshot
 * @return {number} number of entries written
 */
export function applyAllRaMemoriesSnapshot(stores, snapshot) {
    if (!snapshot) {
        return 0;
    }
    let written = applyEntries(stores.candexSessionIds, snapshot.candexSessionIds);
    written += applyEntries(stores.nestBuySession, snapshot.nestBuySessionInfo);
    written += applyEntries(stores.cxmlBuyerCookies, snapshot.cxmlBuyerCookies);
    written += applyEntries(stores.systemAliasByUser, snapshot.systemAliasByUser);
    return written;
}

/**
 * Merge peer All RA snapshots into the local stores. A later peer wins on the same key.
 *
 * @param {string} selfBackendId
 * @param {*} snapshots
 * @param {{candexSessionIds: {set: Function}, nestBuySession: {set: Function}, cxmlBuyerCookies: {set: Function}, systemAliasByUser: {set: Function}}} stores
 * @return {{peerCount: number, applied: number}}
 */
export function applyAllRaPeerSnapshots(selfBackendId, snapshots, stores) {
    const peers = peerSnapshots(selfBackendId, snapshots);
    let applied = 0;
    for (const snapshot of peers) {
        applied += applyAllRaMemoriesSnapshot(stores, snapshot);
    }
    return {peerCount: peers.length, applied};
}

/**
 * Union peer PR tags into the local set of PRs being submitted.
 *
 * @param {{add: function(string): *}} prsBeingSubmitted
 * @param {*} raw set-like list of PR tags, or a snapshot `{prsBeingSubmitted}`
 * @return {number} number of values written
 */
export function applySubmitPrSnapshot(prsBeingSubmitted, raw) {
    const isSnapshot = isSubmitPrSnapshot(raw);
    const tags = isSnapshot ? raw.prsBeingSubmitted : raw;
    return applySetValues(prsBeingSubmitted, tags);
}

/**
 * True when `raw` is a peer snapshot `{prsBeingSubmitted}` rather than the set itself.
 *
 * @param {*} raw
 * @return {boolean}
 */
function isSubmitPrSnapshot(raw) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw) || raw instanceof Set) {
        return false;
    }
    return Object.prototype.hasOwnProperty.call(raw, 'prsBeingSubmitted');
}

/**
 * Merge every non-self submit-PR snapshot into the local set.
 *
 * @param {string} selfBackendId
 * @param {*} snapshots
 * @param {{add: function(string): *}} prsBeingSubmitted
 * @return {{peerCount: number, applied: number}}
 */
export function applySubmitPrPeerSnapshots(selfBackendId, snapshots, prsBeingSubmitted) {
    const peers = peerSnapshots(selfBackendId, snapshots);
    let applied = 0;
    for (const snapshot of peers) {
        applied += applySubmitPrSnapshot(prsBeingSubmitted, snapshot);
    }
    return {peerCount: peers.length, applied};
}

/**
 * Copy shared user-service state. Skips onlineFrontends. Unions frontend ids per PR.
 *
 * @param {{frontendsCheckingAPR: {set: function(string, string[]): *, get: function(string): string[]|undefined}, frontendIdUserLink: {set: function(string, {userId: string, userFullName: string}): *}}} stores
 * @param {Object} snapshot
 * @return {number} number of entries written
 */
export function applyUserServiceSnapshot(stores, snapshot) {
    if (!snapshot || snapshot.ignore) {
        return 0;
    }
    let applied = 0;
    if (stores.frontendsCheckingAPR && typeof stores.frontendsCheckingAPR.set === 'function') {
        for (const [key, value] of toEntries(snapshot.frontendsCheckingAPR)) {
            if (!Array.isArray(value)) {
                continue;
            }
            let current;
            if (typeof stores.frontendsCheckingAPR.get === 'function') {
                current = stores.frontendsCheckingAPR.get(key);
            }
            stores.frontendsCheckingAPR.set(key, unionFrontendIds(current, value));
            applied++;
        }
    }
    if (stores.frontendIdUserLink && typeof stores.frontendIdUserLink.set === 'function') {
        for (const [key, value] of toEntries(snapshot.frontendIdUserLink)) {
            if (!value || typeof value !== 'object') {
                continue;
            }
            stores.frontendIdUserLink.set(key, {userId: value.userId, userFullName: value.userFullName});
            applied++;
        }
    }
    return applied;
}

/**
 * Merge peer user-service snapshots into the local stores.
 *
 * @param {string} selfBackendId
 * @param {*} snapshots
 * @param {{frontendsCheckingAPR: {set: Function}, frontendIdUserLink: {set: Function}}} stores
 * @return {{peerCount: number, applied: number}}
 */
export function applyUserServicePeerSnapshots(selfBackendId, snapshots, stores) {
    const peers = peerSnapshots(selfBackendId, snapshots);
    let applied = 0;
    for (const snapshot of peers) {
        applied += applyUserServiceSnapshot(stores, snapshot);
    }
    return {peerCount: peers.length, applied};
}

/**
 * Append frontend ids that are not already present, preserving the current rank order.
 *
 * @param {string[]|undefined} existing ids already stored for this PR, in rank order
 * @param {string[]} incoming ids from one peer snapshot
 * @return {string[]} a new array
 */
export function unionFrontendIds(existing, incoming) {
    const merged = Array.isArray(existing) ? existing.slice() : [];
    for (const id of incoming) {
        if (!merged.includes(id)) {
            merged.push(id);
        }
    }
    return merged;
}

/**
 * Remove the first occurrence of a frontend id from every PR list.
 *
 * @param {Map<string, string[]>} frontendsCheckingAPR
 * @param {string} frontendId
 * @return {string[]} PR tags this frontend is removed from
 */
export function removeFrontendFromPrLists(frontendsCheckingAPR, frontendId) {
    const updatedPrs = [];
    if (!frontendId || !frontendsCheckingAPR) {
        return updatedPrs;
    }
    for (const [pr, frontends] of frontendsCheckingAPR.entries()) {
        if (Array.isArray(frontends) && frontends.includes(frontendId)) {
            frontends.splice(frontends.indexOf(frontendId), 1);
            updatedPrs.push(pr);
            if (frontends.length === 0) {
                frontendsCheckingAPR.delete(pr);
            }
        }
    }
    return updatedPrs;
}

/**
 * This frontend is now checking exactly `openPrs`.
 * Other frontends keep their rank. A newly added frontend is appended.
 *
 * @param {Map<string, string[]>} frontendsCheckingAPR
 * @param {string} frontendId
 * @param {string[]} openPrs
 * @return {void}
 */
export function applyOpenPrs(frontendsCheckingAPR, frontendId, openPrs) {
    openPrs.forEach((openPr) => {
        if (!frontendsCheckingAPR.has(openPr)) {
            frontendsCheckingAPR.set(openPr, [frontendId]);
        } else if (!frontendsCheckingAPR.get(openPr).includes(frontendId)) {
            frontendsCheckingAPR.get(openPr).push(frontendId);
        }
    });
    for (const [openPr, frontends] of frontendsCheckingAPR.entries()) {
        if (frontends.includes(frontendId) && !openPrs.includes(openPr)) {
            while (frontends.includes(frontendId)) {
                frontends.splice(frontends.indexOf(frontendId), 1);
            }
        }
        if (frontends.length === 0) {
            frontendsCheckingAPR.delete(openPr);
        }
    }
}

/**
 * Ordered log of live mutations recorded during startup sync.
 *
 * @return {{start: function(): void, isRecording: function(): boolean, record: function(Object): void, drain: function(): Array<Object>}}
 */
export function createSyncMutationLog() {
    /** @type {Array<Object>} */
    const entries = [];
    let recording = false;
    return {
        /**
         * Start recording and clear the log.
         * @return {void}
         */
        start() {
            entries.length = 0;
            recording = true;
        },
        /**
         * @return {boolean}
         */
        isRecording() {
            return recording;
        },
        /**
         * @param {Object} mutation
         * @return {void}
         */
        record(mutation) {
            if (recording && mutation) {
                entries.push(mutation);
            }
        },
        /**
         * Stop recording and return the mutations in arrival order.
         * @return {Array<Object>}
         */
        drain() {
            recording = false;
            return entries.splice(0, entries.length);
        },
    };
}

/**
 * Replay All RA mutations onto the local stores.
 *
 * @param {{candexSessionIds: {set: Function, delete: Function}, nestBuySession: {set: Function, delete: Function}, cxmlBuyerCookies: {set: Function, delete: Function}, systemAliasByUser: {set: Function}}} stores
 * @param {Array<Object>} mutations
 * @return {void}
 */
export function replayAllRaMutations(stores, mutations) {
    for (const mutation of mutations || []) {
        switch (mutation.op) {
        case SYNC_MUTATION.SET_CANDEX:
            stores.candexSessionIds.set(mutation.key, mutation.value);
            break;
        case SYNC_MUTATION.SET_NESTBUY:
            stores.nestBuySession.set(mutation.key, mutation.value);
            break;
        case SYNC_MUTATION.DELETE_NESTBUY:
            stores.nestBuySession.delete(mutation.key);
            break;
        case SYNC_MUTATION.SET_CXML:
            stores.cxmlBuyerCookies.set(mutation.key, mutation.value);
            break;
        case SYNC_MUTATION.DELETE_CXML:
            stores.cxmlBuyerCookies.delete(mutation.key);
            break;
        case SYNC_MUTATION.SET_ALIAS:
            stores.systemAliasByUser.set(mutation.key, mutation.value);
            break;
        default:
            break;
        }
    }
}

/**
 * Replay submit-PR adds and deletes onto the local set.
 *
 * @param {{add: function(string): *, delete: function(string): *}} prsBeingSubmitted
 * @param {Array<Object>} mutations
 * @return {void}
 */
export function replaySubmitPrMutations(prsBeingSubmitted, mutations) {
    for (const mutation of mutations || []) {
        if (mutation.op === SYNC_MUTATION.ADD_PR) {
            prsBeingSubmitted.add(mutation.prTag);
        } else if (mutation.op === SYNC_MUTATION.DELETE_PR) {
            prsBeingSubmitted.delete(mutation.prTag);
        }
    }
}

/**
 * Replay user-service mutations onto the post-snapshot stores.
 * `onlineFrontends` is not touched.
 *
 * @param {{frontendsCheckingAPR: Map<string, string[]>, frontendIdUserLink: Map<string, {userId: string, userFullName: string}>}} stores
 * @param {Array<Object>} mutations
 * @return {void}
 */
export function replayUserServiceMutations(stores, mutations) {
    for (const mutation of mutations || []) {
        switch (mutation.op) {
        case SYNC_MUTATION.SET_USER_LINK:
            stores.frontendIdUserLink.set(mutation.frontendId, {
                userId: mutation.userId,
                userFullName: mutation.userFullName,
            });
            break;
        case SYNC_MUTATION.UNSUBSCRIBE_FRONTEND:
            removeFrontendFromPrLists(stores.frontendsCheckingAPR, mutation.frontendId);
            break;
        case SYNC_MUTATION.UNLOCK_ALL_PRS:
            stores.frontendsCheckingAPR.clear();
            break;
        case SYNC_MUTATION.OPEN_PRS:
            applyOpenPrs(stores.frontendsCheckingAPR, mutation.frontendId, mutation.openPrs);
            break;
        default:
            break;
        }
    }
}

/**
 * Startup mutation logs, keyed by the live frontendsCheckingAPR map.
 * @type {WeakMap<Object, ReturnType<typeof createSyncMutationLog>>}
 */
const userServiceSyncLogs = new WeakMap();

/**
 * Start recording live user-service mutations until {@link finishUserServiceStartupSync}.
 *
 * @param {Map} frontendsCheckingAPR the live map passed to the synchronize brick
 * @return {ReturnType<typeof createSyncMutationLog>}
 */
export function beginUserServiceStartupSync(frontendsCheckingAPR) {
    const log = createSyncMutationLog();
    log.start();
    userServiceSyncLogs.set(frontendsCheckingAPR, log);
    return log;
}

/**
 * Record a mutation when this map's startup sync is still open.
 *
 * @param {Map} frontendsCheckingAPR
 * @param {Object} mutation
 * @return {void}
 */
export function recordUserServiceStartupMutation(frontendsCheckingAPR, mutation) {
    const log = userServiceSyncLogs.get(frontendsCheckingAPR);
    if (log) {
        log.record(mutation);
    }
}

/**
 * Apply a peer snapshot, then replay mutations recorded during the collect.
 * An ignored snapshot leaves the live maps unchanged.
 *
 * @param {{frontendsCheckingAPR: Map, frontendIdUserLink: Map}} stores
 * @param {Object=} snapshot
 * @return {number} entries written from the snapshot, before replay
 */
export function finishUserServiceStartupSync(stores, snapshot) {
    const log = userServiceSyncLogs.get(stores.frontendsCheckingAPR);
    const mutations = log ? log.drain() : [];
    if (log) {
        userServiceSyncLogs.delete(stores.frontendsCheckingAPR);
    }
    if (!snapshot || snapshot.ignore) {
        return 0;
    }
    const applied = applyUserServiceSnapshot(stores, snapshot);
    replayUserServiceMutations(stores, mutations);
    return applied;
}

/**
 * Record that this replica received its own multicast.
 *
 * @param {Map<string, PendingCollection>} pendingCollections
 * @param {string=} correlationId
 * @return {void}
 */
export function noteOwnMulticast(pendingCollections, correlationId) {
    const pending = pendingCollections.get(correlationId);
    if (pending) {
        pending.sawOwnMulticast = true;
    }
}

/**
 * Store a peer snapshot on the collection this replica is coordinating.
 *
 * @param {Map<string, PendingCollection>} pendingCollections
 * @param {string} selfId this replica, which must be `message.coordinatorId`
 * @param {Object} message
 * @param {string=} message.correlationId
 * @param {string=} message.coordinatorId
 * @param {string=} message.uuid sender id, used when the snapshot has no backendId
 * @param {Object=} message.memories snapshot payload
 * @param {Object=} message.snapshot alias of `memories`
 * @return {boolean} true when a peer snapshot is stored
 */
export function recordCollectReply(pendingCollections, selfId, message) {
    if (!message || message.coordinatorId !== selfId) {
        return false;
    }
    const pending = pendingCollections.get(message.correlationId);
    const snapshot = message.memories ?? message.snapshot;
    if (!pending || !snapshot || typeof snapshot !== 'object' || Array.isArray(snapshot)) {
        return false;
    }
    const peerId = snapshot.backendId ?? message.uuid;
    if (!peerId || peerId === selfId) {
        return false;
    }
    pending.results.set(peerId, snapshot);
    if (pending.results.size >= pending.expected) {
        pending.done();
    } else if (typeof pending.armPeerGrace === 'function') {
        pending.armPeerGrace();
    }
    return true;
}

/**
 * Multicast a collect request and gather peer snapshots.
 * With peerGraceMs, finish shortly after the first peer. Otherwise wait for `expected` replicas or timeoutMs. Republish once if this replica misses its own multicast.
 *
 * @param {Object} options
 * @param {function(Object): Promise<void>} options.multicast publishes one collect request
 * @param {Object} options.requestBody topic and extra fields; correlationId and uuid are added
 * @param {string} options.selfId this replica's id
 * @param {Object} options.selfSnapshot snapshot of this replica when the collection starts
 * @param {number} options.expected replicas expected to answer, this one included
 * @param {number} options.timeoutMs how long to wait for peers after the request is published when fewer than `expected` answer
 * @param {Map<string, PendingCollection>} options.pendingCollections in-flight collections keyed by correlation id
 * @param {number=} options.peerGraceMs startup only: finish this long after the first peer snapshot
 * @param {number=} options.echoGraceMs how long to wait for our own multicast before publishing again
 * @param {function(number): Promise<void>=} options.delay injectable wait, used by tests
 * @return {Promise<Map<string, Object>>} snapshots keyed by backend id, this replica included
 */
export function collectPeerSnapshots({
    multicast,
    requestBody,
    selfId,
    selfSnapshot,
    expected,
    timeoutMs,
    pendingCollections,
    peerGraceMs = 0,
    echoGraceMs = SYNC_ECHO_GRACE_MS,
    delay = defaultDelay,
}) {
    const correlationId = crypto.randomUUID();
    const results = new Map([[selfId, selfSnapshot]]);

    return new Promise((resolve) => {
        /** @type {?ReturnType<typeof setTimeout>} */
        let timer = null;
        /** @type {?ReturnType<typeof setTimeout>} */
        let peerGraceTimer = null;
        let settled = false;

        /**
         * Resolve the collection once.
         * @return {void}
         */
        const finish = () => {
            if (settled) {
                return;
            }
            settled = true;
            if (timer !== null) {
                clearTimeout(timer);
                timer = null;
            }
            if (peerGraceTimer !== null) {
                clearTimeout(peerGraceTimer);
                peerGraceTimer = null;
            }
            pendingCollections.delete(correlationId);
            resolve(results);
        };

        /**
         * Finish shortly after the first peer. Later calls do not extend the wait.
         * @return {void}
         */
        const armPeerGrace = () => {
            if (settled || peerGraceTimer !== null || peerGraceMs <= 0) {
                return;
            }
            peerGraceTimer = setTimeout(finish, peerGraceMs);
        };

        /** @type {PendingCollection} */
        const pending = {
            results,
            expected,
            sawOwnMulticast: false,
            done: finish,
            armPeerGrace: peerGraceMs > 0 ? armPeerGrace : undefined,
        };
        pendingCollections.set(correlationId, pending);

        if (results.size >= expected) {
            finish();
            return;
        }

        const body = {...requestBody, correlationId, uuid: selfId};
        const grace = Math.min(Math.max(echoGraceMs, 0), Math.max(timeoutMs, 0));

        (async () => {
            try {
                await multicast(body);
            } catch (e) {
                console.error('Error multicasting shared memory sync request:', e);
                finish();
                return;
            }

            await delay(grace);
            const stillPending = !settled && pendingCollections.get(correlationId) === pending;
            if (stillPending && !pending.sawOwnMulticast) {
                try {
                    await multicast(body);
                } catch (e) {
                    console.error('Error republishing shared memory sync request:', e);
                }
            }

            if (settled) {
                return;
            }
            if (results.size >= expected) {
                finish();
                return;
            }
            timer = setTimeout(finish, Math.max(timeoutMs - grace, 0));
        })();
    });
}

/**
 * Log whether startup sync found a peer.
 *
 * @param {string} serviceName service id, used as the log prefix
 * @param {{peerCount: number, applied: number}} result
 * @return {void}
 */
export function logSharedMemorySync(serviceName, result) {
    if (result.peerCount > 0) {
        console.log(`${serviceName}: synchronized shared memory from ${result.peerCount} peer(s) (${result.applied} entries).`);
    } else {
        console.log(`${serviceName}: sole replica, starting with empty shared memory.`);
    }
}
